import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useSnackbar from '@hooks/useSnackbar';
import { useSseStream, type SseEvent } from '@hooks/useSseStream';
import {
  acceptRunnerClaim,
  cancelNodeRun,
  cancelRun,
  completeManualNode,
  decideGate,
  getRun,
  listRuns,
  prepareManualOutputs,
  prepareRun,
  startNodeRun,
  startPreparedRun,
  subscribeToRunStream,
  type GateDecisionValue,
  type ManualOutputPrepareResponse,
  type ManualOutputUpload,
  type PrepareRunInputDescriptor,
  type PrepareRunResponse,
  type ProcessRunDto,
  type RunInputValue,
} from '@api/processRuns';
import type { RunConfigFormValue } from '../run/RunConfigForm';
import { processDetailQueryKey } from '../../hooks/useProcessDetail';
import { processRunsListQueryKey } from '../../hooks/processRunsQueryKeys';

import type {
  ManualOutputFile,
  ProcessRunSessionState,
  RunMode,
  UseProcessRunSessionReturn,
} from './types';
import { errorCode, errorMessage, handleManualError, handleStartError } from './errors';
import { initialState, preserveTranscripts, seedFromRunDetail, terminalOutcome } from './state';
import { computeReconnectBackoff, sleepUntilAborted } from './reconnect';
import { createEventHandler } from './eventHandler';

export async function connectAndRead(
  processId: string,
  runId: string,
  streamController: AbortController,
  loopController: AbortController,
  readStream: (reader: ReadableStreamDefaultReader<Uint8Array>) => Promise<boolean>,
): Promise<boolean> {
  let response: Response | null = null;
  try {
    response = await subscribeToRunStream(processId, runId, {
      signal: streamController.signal,
    });
  } catch {
    if (loopController.signal.aborted) return false;
  }

  if (!response?.ok || !response.body) return false;

  try {
    await readStream(response.body.getReader());
    return true;
  } catch {
    return false;
  }
}

/**
 * Owns the full run-engine state machine for one editor page.
 *
 * Responsibilities:
 * - Mutates: start full-graph / single-node runs, cancel, decide gates.
 * - Subscribes to per-run SSE, folds events into
 *   {@link ProcessRunSessionState}.
 * - Reconnects on mount when a run is active.
 * - On terminal events invalidates React Query caches.
 */
export function useProcessRunSession(args: {
  readonly processId: string | undefined;
  readonly hasActiveTestRun: boolean;
  readonly isPublished?: boolean;
  /**
   * Given a gate nodeId and the chosen decision, return the immediate
   * downstream node ids on the unchosen branch. Used by the
   * {@code gate_decided} handler to mark them as `skipped`.
   */
  readonly unchosenBranchNodeIds?: (
    gateNodeId: string,
    decision: string,
  ) => readonly string[];
  /** Reconverge the canvas after a start was rejected as built on an older graph. */
  readonly onStaleGraph?: () => void;
}): UseProcessRunSessionReturn {
  const {
    processId,
    hasActiveTestRun,
    isPublished = false,
    unchosenBranchNodeIds: unchosenBranchNodeIdsProp,
    onStaleGraph,
  } = args;
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useSnackbar();
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const unchosenBranchRef = useRef(unchosenBranchNodeIdsProp);
  const onStaleGraphRef = useRef(onStaleGraph);
  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    unchosenBranchRef.current = unchosenBranchNodeIdsProp;
    onStaleGraphRef.current = onStaleGraph;
  }, [showError, showSuccess, unchosenBranchNodeIdsProp, onStaleGraph]);

  const notifyStaleGraph = useCallback(() => onStaleGraphRef.current?.(), []);

  const [state, setState] = useState<ProcessRunSessionState>(initialState);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const sseAbortRef = useRef<AbortController | null>(null);
  const reconnectAbortRef = useRef<AbortController | null>(null);
  const reconnectAttemptedRef = useRef(false);
  const lastRunSeedAttemptedRef = useRef(false);

  const mountedRef = useRef(false);
  const safeSetState = useCallback(
    (updater: (prev: ProcessRunSessionState) => ProcessRunSessionState) => {
      if (!mountedRef.current) return;
      setState(updater);
    },
    [],
  );

  // Render-time reset on processId change (React's "reset state when a
  // prop changes" pattern). MUST be synchronous with render so
  // consumers — notably useLastCompletedRunLog — never observe the new
  // processId paired with a runId from the previous process: that
  // mismatch would fire GET /processes/{newId}/runs/{oldRunId}, which
  // the backend correctly answers 404. A post-commit useEffect would
  // leave exactly that stale-render window open.
  const [prevProcessId, setPrevProcessId] = useState(processId);
  if (prevProcessId !== processId) {
    setPrevProcessId(processId);
    setState(initialState());
  }

  // Effect-side resets / aborts for processId change. The one-shot
  // ref flags only gate other useEffects, so a post-commit reset is
  // soundly ordered before any reconnect / seed effect re-evaluates.
  // Cleanup aborts the prior process's SSE stream + reconnect loop.
  useEffect(() => {
    reconnectAttemptedRef.current = false;
    lastRunSeedAttemptedRef.current = false;
    return () => {
      sseAbortRef.current?.abort();
      sseAbortRef.current = null;
      reconnectAbortRef.current?.abort();
      reconnectAbortRef.current = null;
    };
  }, [processId]);

  // ── SSE event handler ──────────────────────────────────────────────────
  const abortStream = useCallback(() => {
    sseAbortRef.current?.abort();
    sseAbortRef.current = null;
  }, []);
  const abortReconnect = useCallback(() => {
    reconnectAbortRef.current?.abort();
    reconnectAbortRef.current = null;
  }, []);

  const eventHandlerRef = useRef<((event: SseEvent) => void) | null>(null);
  useEffect(() => {
    eventHandlerRef.current = createEventHandler({
      processId,
      queryClient,
      safeSetState,
      abortStream,
      abortReconnect,
      unchosenBranchNodeIds: (gateNodeId, decision) =>
        unchosenBranchRef.current?.(gateNodeId, decision) ?? [],
    });
  }, [processId, queryClient, safeSetState, abortStream, abortReconnect]);

  const handleEvent = useCallback(
    (event: SseEvent) => { eventHandlerRef.current?.(event); },
    [],
  );

  const { readStream, abort: abortReader } = useSseStream(handleEvent);

  // ── SSE reconnect loop ─────────────────────────────────────────────────

  const reseedWhileDisconnected = useCallback(
    (pid: string, runId: string): void => {
      queryClient
        .invalidateQueries({ queryKey: processDetailQueryKey(pid) })
        .catch(() => undefined);
      getRun(pid, runId)
        .then((detail) => {
          const seeded = seedFromRunDetail(detail);
          safeSetState((prev) => {
            if (prev.active?.mode.runId !== runId) return prev;
            return {
              ...prev,
              active: { ...prev.active, runStatus: seeded.runStatus },
              perNode: preserveTranscripts(prev.perNode, seeded.perNode),
              gateWaiting: seeded.gateWaiting,
              runnerWaiting: seeded.runnerWaiting,
              manualWaiting: seeded.manualWaiting,
            };
          });
        })
        .catch(() => undefined);
    },
    [queryClient, safeSetState],
  );

  const openRunStream = useCallback(
    async (runId: string): Promise<void> => {
      if (!processId) return;
      sseAbortRef.current?.abort();
      sseAbortRef.current = null;
      reconnectAbortRef.current?.abort();

      const loopController = new AbortController();
      reconnectAbortRef.current = loopController;
      let attempt = 0;

      while (!loopController.signal.aborted) {
        const streamController = new AbortController();
        sseAbortRef.current = streamController;
        safeSetState((prev) => ({
          ...prev,
          streamDisconnected: false,
          isReconnecting: false,
        }));

        const openedSuccessfully = await connectAndRead(
          processId, runId, streamController, loopController, readStream,
        );

        if (loopController.signal.aborted) {
          if (sseAbortRef.current === streamController) sseAbortRef.current = null;
          return;
        }
        if (sseAbortRef.current === streamController) sseAbortRef.current = null;
        if (stateRef.current.active?.mode.runId !== runId) return;

        safeSetState((prev) => ({
          ...prev,
          streamDisconnected: true,
          isReconnecting: true,
        }));

        reseedWhileDisconnected(processId, runId);

        const delay = openedSuccessfully ? 0 : computeReconnectBackoff(attempt);
        attempt = openedSuccessfully ? 0 : attempt + 1;
        await sleepUntilAborted(delay, loopController.signal);
      }
    },
    [processId, readStream, safeSetState, reseedWhileDisconnected],
  );

  // ── Mutation helpers ────────────────────────────────────────────────────

  const doActivateRun = useCallback(
    (started: ProcessRunDto, mode: RunMode): void => {
      safeSetState((prev) => ({
        ...prev,
        startInFlight: false,
        active: {
          mode,
          runStatus: started.status,
          startedAt: started.startedAt ?? started.createdAt,
          runNumber: started.runNumber,
          isTest: started.isTest,
        },
        perNode: new Map(),
        gateWaiting: null,
        runnerWaiting: null,
        lastTerminalRun: null,
        streamDisconnected: false,
        isReconnecting: false,
      }));
      openRunStream(started.runId).catch(() => undefined);
      if (processId) {
        queryClient
          .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
          .catch(() => undefined);
        queryClient
          .invalidateQueries({ queryKey: processRunsListQueryKey(processId) })
          .catch(() => undefined);
      }
    },
    [openRunStream, processId, queryClient, safeSetState],
  );

  const doHandleNodeRunError = useCallback(
    (err: unknown, pid: string): void => {
      const code = errorCode(err);
      const msg = errorMessage(err, 'Failed to start node run');
      if (code === 'TEST_RUN_ACTIVE') {
        showErrorRef.current('A test run is already in progress for this process.');
        queryClient
          .invalidateQueries({ queryKey: processDetailQueryKey(pid) })
          .catch(() => undefined);
      } else if (code === 'PROCESS_NOT_OWNER') {
        showErrorRef.current('Only the owner can run a draft process.');
      } else if (code === 'INSUFFICIENT_CREDITS') {
        showErrorRef.current('Not enough credits to start this run.');
      } else if (code === 'UPSTREAM_NOT_COMPUTED') {
        const tip = msg
          ? msg + ' Tip: provide values for any upstream Input nodes (and decide upstream Gates) first.'
          : 'An upstream Input or Gate has not been resolved yet. Provide values for upstream Inputs and decide upstream Gates, then try again.';
        showErrorRef.current(tip);
      } else {
        showErrorRef.current(msg);
      }
      safeSetState((prev) => ({ ...prev, startInFlight: false }));
    },
    [queryClient, safeSetState],
  );

  // ── Mutations ──────────────────────────────────────────────────────────

  const startFullRun = useCallback(
    async (args2: {
      readonly values: Readonly<Record<string, RunConfigFormValue>>;
      readonly isTest: boolean;
      readonly expectedUpdatedAt?: string;
    }): Promise<void> => {
      if (stateRef.current.startInFlight) return;
      if (!processId) return;
      safeSetState((prev) => ({ ...prev, startInFlight: true, inputError: null }));

      const fileInputs: Record<string, PrepareRunInputDescriptor> = {};
      const startInputs: Record<string, RunInputValue> = {};
      const filesToUpload: Array<{
        readonly portId: string;
        readonly file: File;
        readonly contentType: string;
      }> = [];
      for (const [portId, v] of Object.entries(args2.values)) {
        if (v.kind === 'file') {
          const contentType = v.file.type || 'application/octet-stream';
          fileInputs[portId] = { name: v.file.name, contentType };
          filesToUpload.push({ portId, file: v.file, contentType });
        } else {
          startInputs[portId] = { text: v.text };
        }
      }

      let prepared: PrepareRunResponse;
      try {
        prepared = await prepareRun(
          processId,
          { fileInputs, expectedUpdatedAt: args2.expectedUpdatedAt },
          { test: args2.isTest },
        );
      } catch (err) {
        handleStartError(err, 'Failed to prepare run', safeSetState, queryClient, processId,
          showErrorRef, notifyStaleGraph);
        throw err;
      }

      try {
        await Promise.all(
          filesToUpload.map(async ({ portId, file, contentType }) => {
            const slot = prepared.uploads[portId];
            if (!slot) throw new Error(`No upload slot for port ${portId}`);
            const res = await fetch(slot.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: slot.uploadHeaders,
            });
            if (!res.ok) throw new Error(`Upload failed for ${file.name} (HTTP ${res.status})`);
            startInputs[portId] = { uploaded: true, name: file.name, contentType };
          }),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'File upload failed';
        showErrorRef.current(msg);
        safeSetState((prev) => ({ ...prev, startInFlight: false }));
        throw err;
      }

      let started: ProcessRunDto;
      try {
        started = await startPreparedRun(processId, prepared.runId, { inputs: startInputs });
      } catch (err) {
        handleStartError(err, 'Failed to start run', safeSetState, queryClient, processId,
          showErrorRef, notifyStaleGraph);
        throw err;
      }

      doActivateRun(started, { kind: 'full', runId: started.runId });
    },
    [doActivateRun, notifyStaleGraph, processId, queryClient, safeSetState],
  );

  const startNodeRunHook = useCallback(
    async (nodeId: string, opts?: { readonly inputValue?: RunInputValue }): Promise<void> => {
      if (stateRef.current.startInFlight) return;
      if (!processId) return;
      safeSetState((prev) => ({ ...prev, startInFlight: true }));

      let started: ProcessRunDto;
      try {
        started = await startNodeRun(processId, nodeId, { inputValue: opts?.inputValue });
      } catch (err) {
        doHandleNodeRunError(err, processId);
        throw err;
      }

      doActivateRun(started, { kind: 'node', runId: started.runId, nodeId });
    },
    [doActivateRun, doHandleNodeRunError, processId, safeSetState],
  );

  const cancelActive = useCallback(async (): Promise<void> => {
    if (!processId) return;

    const active = stateRef.current.active;
    let mode = active?.mode ?? null;

    if (!mode) {
      const list = await listRuns(processId, {
        status: 'pending,running,waiting_for_action',
        size: 1,
      });
      const inFlight = list.runs[0];
      if (!inFlight) return;
      mode = inFlight.targetNodeId
        ? { kind: 'node' as const, runId: inFlight.runId, nodeId: inFlight.targetNodeId }
        : { kind: 'full' as const, runId: inFlight.runId };
    }

    try {
      if (mode.kind === 'node') {
        await cancelNodeRun(processId, mode.nodeId);
      } else {
        await cancelRun(processId, mode.runId);
      }
    } catch (err) {
      const code = errorCode(err);
      if (code === 'INSUFFICIENT_ROLE') {
        showErrorRef.current('Only the user who started the run or the owner can cancel it.');
      } else if (code === 'RUN_TERMINAL_STATE') {
        queryClient
          .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
          .catch(() => undefined);
      } else {
        showErrorRef.current(errorMessage(err, 'Failed to cancel run'));
      }
    }
  }, [processId, queryClient]);

  const decideGateHook = useCallback(
    async (decision: GateDecisionValue, comment?: string): Promise<void> => {
      const gw = stateRef.current.gateWaiting;
      if (!gw || !processId) return;
      try {
        await decideGate(processId, gw.runId, gw.nodeId, {
          decision,
          comment,
          iteration: gw.iteration,
        });
        safeSetState((prev) => ({ ...prev, gateWaiting: null }));
        showSuccessRef.current(
          decision === 'approved' ? 'Gate approved.' : 'Gate rejected.',
        );
      } catch (err) {
        const code = errorCode(err);
        if (code === 'INSUFFICIENT_ROLE') {
          showErrorRef.current('Only an admin can decide this gate.');
        } else if (code === 'GATE_REJECTION_COMMENT_REQUIRED') {
          showErrorRef.current(
            'Add a comment explaining why you are rejecting before submitting.',
          );
        } else if (
          code === 'RUN_NOT_WAITING' ||
          code === 'GATE_ALREADY_DECIDED' ||
          code === 'ITERATION_MISMATCH'
        ) {
          safeSetState((prev) => ({ ...prev, gateWaiting: null }));
          queryClient
            .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
            .catch(() => undefined);
        } else {
          showErrorRef.current(errorMessage(err, 'Failed to decide gate'));
        }
        throw err;
      }
    },
    [processId, queryClient, safeSetState],
  );

  const acceptRunnerClaimHook = useCallback(async (): Promise<void> => {
    const rw = stateRef.current.runnerWaiting;
    if (!rw || !processId) return;
    try {
      await acceptRunnerClaim(processId, rw.runId);
      safeSetState((prev) => ({ ...prev, runnerWaiting: null }));
      showSuccessRef.current('Allowed — the run will continue on your machine.');
    } catch (err) {
      const code = errorCode(err);
      if (code === 'INSUFFICIENT_ROLE') {
        showErrorRef.current('Only an assigned user can allow this run on their machine.');
      } else if (code === 'RUN_NOT_WAITING') {
        safeSetState((prev) => ({ ...prev, runnerWaiting: null }));
        queryClient
          .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
          .catch(() => undefined);
      } else {
        showErrorRef.current(errorMessage(err, 'Failed to allow the run on your machine'));
      }
      throw err;
    }
  }, [processId, queryClient, safeSetState]);

  const completeManualHook = useCallback(
    async (outputs: readonly ManualOutputFile[]): Promise<void> => {
      const mw = stateRef.current.manualWaiting;
      if (!mw || !processId) return;
      if (outputs.length === 0) {
        showErrorRef.current('Attach at least one output file before completing.');
        return;
      }

      const prepareBody: Record<string, PrepareRunInputDescriptor> = {};
      for (const { portId, file, contentType } of outputs) {
        prepareBody[portId] = { name: file.name, contentType };
      }

      let prepared: ManualOutputPrepareResponse;
      try {
        prepared = await prepareManualOutputs(processId, mw.runId, mw.nodeId, {
          outputs: prepareBody,
        });
      } catch (err) {
        handleManualError(err, safeSetState, queryClient, processId, showErrorRef);
        throw err;
      }

      try {
        await Promise.all(
          outputs.map(async ({ portId, file }) => {
            const slot = prepared.uploads[portId];
            if (!slot) throw new Error(`No upload slot for port ${portId}`);
            const res = await fetch(slot.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: slot.uploadHeaders,
            });
            if (!res.ok) throw new Error(`Upload failed for ${file.name} (HTTP ${res.status})`);
          }),
        );
      } catch (err) {
        showErrorRef.current(err instanceof Error ? err.message : 'File upload failed');
        throw err;
      }

      const uploads: ManualOutputUpload[] = outputs.map(({ portId, file, contentType }) => ({
        portId,
        name: file.name,
        contentType,
      }));
      try {
        await completeManualNode(processId, mw.runId, mw.nodeId, {
          iteration: mw.iteration,
          outputs: uploads,
        });
        safeSetState((prev) => ({ ...prev, manualWaiting: null }));
        showSuccessRef.current('Manual step completed — the run will continue.');
      } catch (err) {
        handleManualError(err, safeSetState, queryClient, processId, showErrorRef);
        throw err;
      }
    },
    [processId, queryClient, safeSetState],
  );

  const liveStateForNode = useCallback(
    (nodeId: string) => state.perNode.get(nodeId),
    [state.perNode],
  );

  const clearInputError = useCallback(() => {
    safeSetState((prev) => ({ ...prev, inputError: null }));
  }, [safeSetState]);

  const resetLiveStates = useCallback(() => {
    safeSetState((prev) => {
      if (prev.active != null) return prev;
      return { ...prev, perNode: new Map(), lastTerminalRun: null };
    });
  }, [safeSetState]);

  // ── Reconnect-on-mount ─────────────────────────────────────────────────
  // For drafts, reconnect to an active test run when the detail flags one.
  // For published processes, reconnect to any active run (test or production).
  const shouldReconnect = hasActiveTestRun || isPublished;
  useEffect(() => {
    if (!processId || !shouldReconnect || reconnectAttemptedRef.current) return;
    if (stateRef.current.active != null) return;
    reconnectAttemptedRef.current = true;

    const pid = processId;
    const isTestFilter: boolean | undefined = isPublished ? undefined : true;
    listRuns(pid, { status: 'pending,running,waiting_for_action', isTest: isTestFilter, size: 1 })
      .then(async (list) => {
        const inFlight = list.runs[0];
        if (!inFlight) return;
        const mode: RunMode = inFlight.targetNodeId
          ? { kind: 'node', runId: inFlight.runId, nodeId: inFlight.targetNodeId }
          : { kind: 'full', runId: inFlight.runId };

        try {
          const detail = await getRun(pid, inFlight.runId);
          const seeded = seedFromRunDetail(detail);
          safeSetState((prev) => ({
            ...prev,
            active: {
              mode,
              runStatus: seeded.runStatus,
              startedAt: inFlight.startedAt ?? inFlight.createdAt,
              runNumber: inFlight.runNumber,
              isTest: inFlight.isTest,
            },
            perNode: seeded.perNode,
            gateWaiting: seeded.gateWaiting,
            runnerWaiting: seeded.runnerWaiting,
          }));
        } catch {
          safeSetState((prev) => ({
            ...prev,
            active: {
              mode,
              runStatus: inFlight.status,
              startedAt: inFlight.startedAt ?? inFlight.createdAt,
              runNumber: inFlight.runNumber,
              isTest: inFlight.isTest,
            },
          }));
        }

        openRunStream(inFlight.runId).catch(() => undefined);
      })
      .catch(() => undefined);
  }, [processId, shouldReconnect, isPublished, openRunStream, safeSetState]);

  // ── Seed last-completed-run on cold load ───────────────────────────────
  //
  // We need BOTH `lastTerminalRun` (so the ExecutionLog footer renders the
  // last run's outcome / number / outputs) AND `perNode` (so the per-node
  // surfaces — red-outlined failed cards, NodeDetailPanel error banner,
  // and the run-failure rows in the topbar legend — survive a page refresh
  // after a failed/cancelled run). The previous version only seeded
  // `lastTerminalRun` from a cheap `listRuns` call, which is why a hard
  // refresh "lost" the error banner on a failed node — the live state was
  // gone and there was nothing on the cold-load path to repopulate it
  // from the persisted node-state rows.
  useEffect(() => {
    if (!processId || shouldReconnect || lastRunSeedAttemptedRef.current) return;
    if (stateRef.current.active != null || stateRef.current.lastTerminalRun != null) return;
    lastRunSeedAttemptedRef.current = true;

    const pid = processId;
    listRuns(pid, { status: 'completed,failed,cancelled', size: 1 })
      .then(async (list) => {
        const lastRun = list.runs[0];
        if (!lastRun) return;
        const outcome = terminalOutcome(lastRun.status);
        // Fetch the full detail so we can seed per-node statuses, error
        // messages, and artifacts. Best-effort: a failure here still
        // leaves the ExecutionLog and run-number badges seeded from the
        // listRuns response above.
        let seededPerNode: ReturnType<typeof seedFromRunDetail>['perNode'] | null = null;
        try {
          const detail = await getRun(pid, lastRun.runId);
          seededPerNode = seedFromRunDetail(detail).perNode;
        } catch {
          // swallowed — seedless fall-back is still useful
        }
        safeSetState((prev) => {
          if (prev.active != null || prev.lastTerminalRun != null) return prev;
          return {
            ...prev,
            lastTerminalRun: {
              runId: lastRun.runId,
              outcome,
              runNumber: lastRun.runNumber,
              isTest: lastRun.isTest,
            },
            perNode: seededPerNode ?? prev.perNode,
          };
        });
      })
      .catch(() => undefined);
  }, [processId, shouldReconnect, safeSetState]);

  // ── Lifecycle ──────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sseAbortRef.current?.abort();
      sseAbortRef.current = null;
      reconnectAbortRef.current?.abort();
      reconnectAbortRef.current = null;
      abortReader();
    };
  }, [abortReader]);

  return useMemo<UseProcessRunSessionReturn>(
    () => ({
      ...state,
      startFullRun,
      startNodeRun: startNodeRunHook,
      cancelActive,
      decideGate: decideGateHook,
      acceptRunnerClaim: acceptRunnerClaimHook,
      completeManual: completeManualHook,
      liveStateForNode,
      clearInputError,
      resetLiveStates,
    }),
    [
      state,
      startFullRun,
      startNodeRunHook,
      cancelActive,
      decideGateHook,
      acceptRunnerClaimHook,
      completeManualHook,
      liveStateForNode,
      resetLiveStates,
      clearInputError,
    ],
  );
}
